import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useBuilderStore } from '../../stores/builderStore';
import { createField } from '../../lib/fieldRegistry';
import { updateForm } from '../../lib/api';
import FieldPalette from './FieldPalette';
import FormCanvas from './FormCanvas';
import FieldConfigurator from './FieldConfigurator';
import { ErrorBoundary } from './ErrorBoundary';
import FormPreview from './FormPreview';
import FormStyling from './FormStyling';
import PollSettings from './PollSettings';
import ApprovalSettings from './ApprovalSettings';
import DragPreviewPortal from './DragPreviewPortal';
import CollaboratorSettings from './CollaboratorSettings';
import FormScheduleSettings from './FormScheduleSettings';
import SaveResumeSettings from './SaveResumeSettings';
import QuizSettingsModal from './QuizSettingsModal';
import FormSettingsModal from './FormSettingsModal';
import WebhookSettings from './WebhookSettings';
import IntegrationsPanel from '../admin/IntegrationsPanel';
import VersionHistory from './VersionHistory';
import ConditionalLogicBuilder from './ConditionalLogicBuilder';
import { useThemeStore } from '../../stores/themeStore';
import {
  isPaletteDragData,
  isCanvasFieldDragData,
  isReorderTargetDropData,
  isLayoutColumnDropData,
  isLayoutColumnFieldDragData,
  isColumnFieldReorderDropData,
  isRepeatableColumnDropData,
  isRepeatableColumnFieldDragData,
  isRepeatableFieldReorderDropData,
  isCanvasDropData,
} from '../../utils/typeGuards';

interface BuilderProps {
  formId?: string;
}

export default function Builder({ formId }: BuilderProps) {
  const { t } = useTranslation('builder');
  const {
    addField,
    setDragging,
    selectedFieldId,
    removeField,
    duplicateField,
    formSchema,
    hasUnsavedChanges,
    markAsSaved,
    reorderFields,
    addFieldToLayoutColumn,
    removeFieldFromLayoutColumn,
    moveFieldFromCanvasToLayoutColumn,
    moveFieldBetweenLayoutColumns,
    reorderFieldsInLayoutColumn,
    addFieldToRepeatableColumn,
    moveFieldFromCanvasToRepeatableColumn,
    moveFieldBetweenRepeatableColumns,
    reorderFieldsInRepeatableColumn,
  } = useBuilderStore();
  useThemeStore(); // Keep store subscription for dark mode reactivity
  const { undo, redo } = useBuilderStore.temporal.getState();

  // Check if this is a quiz-type form (has quiz mode enabled)
  const isQuizForm = formSchema.settings.quiz?.enabled === true;
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isStylingOpen, setIsStylingOpen] = useState(false);
  const [isFormSettingsOpen, setIsFormSettingsOpen] = useState(false);
  const [isPollSettingsOpen, setIsPollSettingsOpen] = useState(false);
  const [isApprovalSettingsOpen, setIsApprovalSettingsOpen] = useState(false);
  const [isCollaboratorSettingsOpen, setIsCollaboratorSettingsOpen] = useState(false);
  const [isScheduleSettingsOpen, setIsScheduleSettingsOpen] = useState(false);
  const [isSaveResumeSettingsOpen, setIsSaveResumeSettingsOpen] = useState(false);
  const [isQuizSettingsOpen, setIsQuizSettingsOpen] = useState(false);
  const [isWebhookSettingsOpen, setIsWebhookSettingsOpen] = useState(false);
  const [isIntegrationsOpen, setIsIntegrationsOpen] = useState(false);
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  const [isConditionalLogicOpen, setIsConditionalLogicOpen] = useState(false);
  const [showMobilePalette, setShowMobilePalette] = useState(false);
  const [showMobileConfig, setShowMobileConfig] = useState(false);

  // Subscribe to temporal store changes
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  useEffect(() => {
    const unsubscribe = useBuilderStore.temporal.subscribe((state) => {
      setCanUndo(state.pastStates.length > 0);
      setCanRedo(state.futureStates.length > 0);
    });
    return unsubscribe;
  }, []);

  // Keyboard shortcuts
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
      return;
    }

    if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
      e.preventDefault();
      undo();
    }

    if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
      e.preventDefault();
      redo();
    }

    if ((e.key === 'Delete' || e.key === 'Backspace') && selectedFieldId) {
      e.preventDefault();
      removeField(selectedFieldId);
    }

    if ((e.ctrlKey || e.metaKey) && e.key === 'd' && selectedFieldId) {
      e.preventDefault();
      duplicateField(selectedFieldId);
    }
  }, [selectedFieldId, removeField, duplicateField, undo, redo]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handlePreview = useCallback(async () => {
    // Auto-save before preview if there are unsaved changes
    if (hasUnsavedChanges && formId) {
      try {
        await updateForm(formId, { schema: formSchema });
        markAsSaved();
      } catch (error) {
        console.error('Failed to save before preview:', error);
        // Continue with preview even if save fails
      }
    }
    setIsPreviewOpen(true);
  }, [hasUnsavedChanges, formId, formSchema, markAsSaved]);

  const handleClosePreview = useCallback(() => {
    setIsPreviewOpen(false);
    document.body.style.overflow = '';
  }, []);

  // Global drag and drop monitor with Pragmatic
  useEffect(() => {
    // ESC key handler to cancel drag
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDragging(false);
      }
    };

    window.addEventListener('keydown', handleEscapeKey);

    const cleanup = monitorForElements({
      onDragStart() {
        setDragging(true);
      },
      onDrop({ source, location }) {
        setDragging(false);

        const dropTargets = location.current.dropTargets;
        if (dropTargets.length === 0) {
          return;
        }

        // Pragmatic automatically prioritizes innermost drop target
        const target = dropTargets[0];
        const sourceData = source.data;
        const targetData = target.data;

        // Case 1: Palette → Layout Column
        if (isPaletteDragData(sourceData) && isLayoutColumnDropData(targetData)) {
          // Prevent nested layouts
          if (sourceData.fieldType === 'layout') {
            return;
          }

          const newField = createField(sourceData.fieldType);
          addFieldToLayoutColumn(
            targetData.layoutFieldId,
            targetData.columnIndex,
            newField
          );
          return;
        }

        // Case 2: Palette → Canvas
        if (isPaletteDragData(sourceData) && isCanvasDropData(targetData)) {
          const newField = createField(sourceData.fieldType);
          addField(newField);
          return;
        }

        // Case 3: Reorder on Canvas (with edge detection)
        if (isCanvasFieldDragData(sourceData) && isReorderTargetDropData(targetData)) {
          if (sourceData.fieldId !== targetData.fieldId) {
            // Reorder using store action
            reorderFields(sourceData.fieldId, targetData.fieldId);
          }
          return;
        }

        // Case 4: Canvas Field → Layout Column
        if (isCanvasFieldDragData(sourceData) && isLayoutColumnDropData(targetData)) {
          const currentFields = useBuilderStore.getState().formSchema.fields;
          const fieldToMove = currentFields.find(f => f.id === sourceData.fieldId);

          // Prevent nested layouts
          if (fieldToMove?.type === 'layout') {
            return;
          }

          moveFieldFromCanvasToLayoutColumn(
            sourceData.fieldId,
            targetData.layoutFieldId,
            targetData.columnIndex
          );
          return;
        }

        // Case 5: Reorder within Layout Column (with edge detection)
        if (isLayoutColumnFieldDragData(sourceData) && isColumnFieldReorderDropData(targetData)) {
          // Only reorder if same column
          if (
            sourceData.layoutFieldId === targetData.layoutFieldId &&
            sourceData.columnIndex === targetData.columnIndex &&
            sourceData.fieldId !== targetData.fieldId
          ) {
            reorderFieldsInLayoutColumn(
              sourceData.layoutFieldId,
              sourceData.columnIndex,
              sourceData.fieldId,
              targetData.fieldId
            );
          }
          return;
        }

        // Case 6: Move between Layout Columns
        if (isLayoutColumnFieldDragData(sourceData) && isLayoutColumnDropData(targetData)) {
          // Only move if different column
          if (
            sourceData.layoutFieldId !== targetData.layoutFieldId ||
            sourceData.columnIndex !== targetData.columnIndex
          ) {
            // Use atomic store action to prevent race conditions
            moveFieldBetweenLayoutColumns(
              sourceData.fieldId,
              sourceData.layoutFieldId,
              sourceData.columnIndex,
              targetData.layoutFieldId,
              targetData.columnIndex
            );
          }
          return;
        }

        // Case 7: Palette → Repeatable Column
        if (isPaletteDragData(sourceData) && isRepeatableColumnDropData(targetData)) {
          const newField = createField(sourceData.fieldType);
          addFieldToRepeatableColumn(targetData.repeatableFieldId, targetData.columnIndex, newField);
          return;
        }

        // Case 8: Canvas Field → Repeatable Column
        if (isCanvasFieldDragData(sourceData) && isRepeatableColumnDropData(targetData)) {
          const currentFields = useBuilderStore.getState().formSchema.fields;
          const fieldToMove = currentFields.find(f => f.id === sourceData.fieldId);

          // Prevent nested repeatables and layouts
          if (fieldToMove?.type === 'repeatable' || fieldToMove?.type === 'layout') {
            return;
          }

          moveFieldFromCanvasToRepeatableColumn(
            sourceData.fieldId,
            targetData.repeatableFieldId,
            targetData.columnIndex
          );
          return;
        }

        // Case 9: Reorder within Repeatable Column (with edge detection)
        if (isRepeatableColumnFieldDragData(sourceData) && isRepeatableFieldReorderDropData(targetData)) {
          if (
            sourceData.repeatableFieldId === targetData.repeatableFieldId &&
            sourceData.columnIndex === targetData.columnIndex &&
            sourceData.fieldId !== targetData.fieldId
          ) {
            reorderFieldsInRepeatableColumn(
              sourceData.repeatableFieldId,
              sourceData.columnIndex,
              sourceData.fieldId,
              targetData.fieldId
            );
          }
          return;
        }

        // Case 10: Move between Repeatable Columns
        if (isRepeatableColumnFieldDragData(sourceData) && isRepeatableColumnDropData(targetData)) {
          if (
            sourceData.repeatableFieldId !== targetData.repeatableFieldId ||
            sourceData.columnIndex !== targetData.columnIndex
          ) {
            moveFieldBetweenRepeatableColumns(
              sourceData.fieldId,
              sourceData.repeatableFieldId,
              sourceData.columnIndex,
              targetData.repeatableFieldId,
              targetData.columnIndex
            );
          }
          return;
        }

        // Case 11: Palette → Canvas between fields (edge detection)
        if (isPaletteDragData(sourceData) && isReorderTargetDropData(targetData)) {
          const newField = createField(sourceData.fieldType);
          addField(newField, targetData.fieldId);
          return;
        }
      },
    });

    // Cleanup both ESC key listener and monitor
    return () => {
      window.removeEventListener('keydown', handleEscapeKey);
      cleanup();
    };
  }, [
    addField,
    addFieldToLayoutColumn,
    moveFieldFromCanvasToLayoutColumn,
    removeFieldFromLayoutColumn,
    reorderFields,
    reorderFieldsInLayoutColumn,
    setDragging,
  ]);

  return (
    <>
      {/* Toolbar */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 border-r border-gray-200 dark:border-gray-700 pr-3 mr-3">
            <button
              onClick={() => undo()}
              disabled={!canUndo}
              className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
              title="Undo (Ctrl+Z)"
            >
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
            </button>
            <button
              onClick={() => redo()}
              disabled={!canRedo}
              className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
              title="Redo (Ctrl+Y)"
            >
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
              </svg>
            </button>
          </div>

          <div className="border-l border-gray-200 dark:border-gray-700 pl-3 ml-1">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {t('toolbar.shortcuts')}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsFormSettingsOpen(true)}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-sm"
            title="Form Settings"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {t('toolbar.settings')}
          </button>

          <button
            onClick={() => setIsStylingOpen(true)}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-sm"
            title="Form Styling"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
            </svg>
            {t('toolbar.styling')}
          </button>

          <button
            onClick={() => setIsSaveResumeSettingsOpen(true)}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-sm"
            title="Save & Resume Settings"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
            </svg>
            {t('toolbar.save')}
          </button>

          <button
            onClick={() => setIsWebhookSettingsOpen(true)}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-sm"
            title="Webhook Settings"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            {t('toolbar.webhook')}
          </button>

          <button
            onClick={() => setIsIntegrationsOpen(true)}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-sm"
            title="Third-Party Integrations"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
            </svg>
            {t('toolbar.integrate')}
          </button>

          <button
            onClick={() => setIsVersionHistoryOpen(true)}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-sm"
            title="Version History"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {t('toolbar.history')}
          </button>

          <button
            onClick={() => setIsConditionalLogicOpen(true)}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-sm"
            title="Conditional Logic"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
            </svg>
            {t('toolbar.logic')}
          </button>

          {isQuizForm && (
            <button
              onClick={() => setIsQuizSettingsOpen(true)}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-sm"
              title="Quiz Settings"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {t('toolbar.quizSettings')}
            </button>
          )}

          <div className="w-px h-6 bg-gray-200 dark:bg-gray-700"></div>

          <button
            onClick={handlePreview}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            {t('toolbar.preview')}
          </button>
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-4 py-2 flex justify-around z-40">
        <button
          onClick={() => { setShowMobilePalette(true); setShowMobileConfig(false); }}
          className={`flex flex-col items-center p-2 rounded-lg ${showMobilePalette ? 'text-primary-600 bg-primary-50 dark:bg-primary-900/20' : 'text-gray-500 dark:text-gray-400'}`}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span className="text-xs mt-1">{t('mobile.addField')}</span>
        </button>
        <button
          onClick={() => { setShowMobilePalette(false); setShowMobileConfig(false); }}
          className={`flex flex-col items-center p-2 rounded-lg ${!showMobilePalette && !showMobileConfig ? 'text-primary-600 bg-primary-50 dark:bg-primary-900/20' : 'text-gray-500 dark:text-gray-400'}`}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6z" />
          </svg>
          <span className="text-xs mt-1">{t('mobile.canvas')}</span>
        </button>
        <button
          onClick={() => { setShowMobileConfig(true); setShowMobilePalette(false); }}
          className={`flex flex-col items-center p-2 rounded-lg ${showMobileConfig ? 'text-primary-600 bg-primary-50 dark:bg-primary-900/20' : 'text-gray-500 dark:text-gray-400'}`}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="text-xs mt-1">{t('mobile.configure')}</span>
        </button>
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:flex h-[calc(100vh-11rem)]">
        <ErrorBoundary componentName="FieldPalette">
          <FieldPalette />
        </ErrorBoundary>
        <ErrorBoundary componentName="FormCanvas">
          <FormCanvas />
        </ErrorBoundary>
        <ErrorBoundary componentName="FieldConfigurator">
          <FieldConfigurator />
        </ErrorBoundary>
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden h-[calc(100vh-14rem)] relative">
        {showMobilePalette && (
          <div className="absolute inset-0 z-30 bg-white dark:bg-gray-900 overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 dark:text-white">{t('mobile.addField')}</h2>
              <button
                onClick={() => setShowMobilePalette(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              >
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <ErrorBoundary componentName="FieldPalette (Mobile)">
              <FieldPalette />
            </ErrorBoundary>
          </div>
        )}

        {showMobileConfig && (
          <div className="absolute inset-0 z-30 bg-white dark:bg-gray-900 overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 dark:text-white">{t('mobile.configureField')}</h2>
              <button
                onClick={() => setShowMobileConfig(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              >
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <ErrorBoundary componentName="FieldConfigurator (Mobile)">
              <FieldConfigurator />
            </ErrorBoundary>
          </div>
        )}

        <div className={showMobilePalette || showMobileConfig ? 'hidden' : ''}>
          <ErrorBoundary componentName="FormCanvas (Mobile)">
            <FormCanvas />
          </ErrorBoundary>
        </div>
      </div>

      {/* Drag Preview Portal */}
      <DragPreviewPortal />

      {/* Modals */}
      <FormPreview isOpen={isPreviewOpen} onClose={handleClosePreview} />

      {formId && (
        <>
          <FormSettingsModal isOpen={isFormSettingsOpen} onClose={() => setIsFormSettingsOpen(false)} />
          <FormStyling formId={formId} isOpen={isStylingOpen} onClose={() => setIsStylingOpen(false)} />
          <PollSettings formId={formId} isOpen={isPollSettingsOpen} onClose={() => setIsPollSettingsOpen(false)} />
          <ApprovalSettings formId={formId} isOpen={isApprovalSettingsOpen} onClose={() => setIsApprovalSettingsOpen(false)} />
          <CollaboratorSettings formId={formId} isOpen={isCollaboratorSettingsOpen} onClose={() => setIsCollaboratorSettingsOpen(false)} />
          <FormScheduleSettings formId={formId} isOpen={isScheduleSettingsOpen} onClose={() => setIsScheduleSettingsOpen(false)} />
          <SaveResumeSettings formId={formId} isOpen={isSaveResumeSettingsOpen} onClose={() => setIsSaveResumeSettingsOpen(false)} />
          <QuizSettingsModal formId={formId} isOpen={isQuizSettingsOpen} onClose={() => setIsQuizSettingsOpen(false)} />
          <WebhookSettings formId={formId} isOpen={isWebhookSettingsOpen} onClose={() => setIsWebhookSettingsOpen(false)} />
          <VersionHistory formId={formId} isOpen={isVersionHistoryOpen} onClose={() => setIsVersionHistoryOpen(false)} />
        </>
      )}

      {formId && (
        <IntegrationsPanel formId={formId} isOpen={isIntegrationsOpen} onClose={() => setIsIntegrationsOpen(false)} />
      )}
      <ConditionalLogicBuilder isOpen={isConditionalLogicOpen} onClose={() => setIsConditionalLogicOpen(false)} />
    </>
  );
}
